import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import {
  UploadStatus, UploadPhoto, FileTypes, PhotoService, UserActivityService, LoaderService, LaunchDarklyService,
  UploadService, MarketingOrderService, PromptDialogService, AppService, FeatureFlags, MarketingOrder, ProductCode,
  PhotoOrder, PhotoOrderStatus, PhotoOrderService,
} from '@lc/core';
import { FileSystemFileEntry, NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { forkJoin, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { MatButton } from '@angular/material/button';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatDivider } from '@angular/material/divider';
import { AsyncPipe } from '@angular/common';
import { UploadResultsDialogService } from '../../dialogs';
import { PhotoRequirementMessageComponent } from '../photo-requirement-message/photo-requirement-message.component';

@Component({
  selector: 'lc-property-photo-upload',
  templateUrl: './property-photo-upload.component.html',
  styleUrls: ['./property-photo-upload.component.scss'],
  imports: [
    NgxFileDropModule,
    MatButton,
    MatProgressBar,
    MatDivider,
    PhotoRequirementMessageComponent,
    AsyncPipe,
  ],
})
export class PropertyPhotoUploadComponent {
  private photoService = inject(PhotoService);
  private activityService = inject(UserActivityService);
  private uploadResults = inject(UploadResultsDialogService);
  private readonly uploadService = inject(UploadService);
  private loaderService = inject(LoaderService);
  private launchDarklyService = inject(LaunchDarklyService);
  private marketingOrderService = inject(MarketingOrderService);
  private readonly promptDialog = inject(PromptDialogService);
  private photoOrderService = inject(PhotoOrderService);

  @Input() readonly marketingOrderId;
  @Input() minimumPhotosRequired = 0;
  @Input() currentPhotoCount = 0;
  @Input() maximumPhotosAllowed = 0;
  @Input() comingSoonMode = false;
  @Input() minimumPhotoSizeRequired = true; // Default to true (enforce 0.3 MB minimum)
  @Output() readonly uploadResult = new EventEmitter<UploadPhoto[]>();

  @ViewChild('content') content: any;
  @ViewChild('photoInput') input: ElementRef<HTMLInputElement>;

  photoColorValidation$: Observable<Boolean>;
  successfulUploads = [];
  failedUploads = [];
  isProcessing = false;
  statuses: UploadStatus[];
  readonly fileTypes = FileTypes; // File validation constraints
  columns: string[] = ['uploads'];
  MAX_NON_PHOTOGRAPHER_PHOTOS_COUNT = 50;
  MAX_PHOTOGRAPHER_PHOTOS_COUNT = 100;
  MAX_ORDER_PHOTOS_COUNT = 150;
  appService = AppService;

  // Cache app type check for performance
  readonly isAgentOrCoordinator: boolean;

  constructor() {
    this.photoColorValidation$ = this.launchDarklyService.getFeature$(FeatureFlags.PHOTO_COLOR_VALIDATION, false);
    this.isAgentOrCoordinator = AppService.isAgentApp || AppService.isCoordinatorApp;
  }

  async getFilesFromUploadEvent(event: NgxFileDropEntry[]) {
    const filePromises = [];
    event.forEach((droppedFile) => {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        const filePromise = new Promise((resolve) => {
          fileEntry.file(resolve);
        });
        filePromises.push(filePromise);
      }
    });
    return Promise.all(filePromises);
  }

  handleDroppedFiles = async (event: NgxFileDropEntry[]) => {
    this.successfulUploads = [];
    this.failedUploads = [];
    const files = await this.getFilesFromUploadEvent(event);
    if (!this.isProcessing && !(await this.isMaxPhotosExceeded(files, this.marketingOrderId))) {
      this.uploadFiles(files);
    }
  };

  handleSelectedFiles = async (event: any) => {
    this.successfulUploads = [];
    this.failedUploads = [];
    if (event.target?.files?.length > 0
      && !(await this.isMaxPhotosExceeded(event.target?.files, this.marketingOrderId))) {
      this.uploadFiles(event.target.files);
    }
  };

  async isMaxPhotosExceeded(files: File[], _marketingOrderId?: string) {
    if (!_marketingOrderId) {
      return false;
    }

    const marketingOrder = await this.marketingOrderService.loadOrder(_marketingOrderId);

    const photographerPhotosCount = marketingOrder?.photos?.filter((photo) => photo.photographerId)?.length ?? 0;
    const totalOrderPhotos = (marketingOrder?.photos?.length ?? 0);

    if (AppService.isPhotoApp) {
      if (this.isPhotoOrderExist(marketingOrder)) {
        const photoOrder: PhotoOrder = await this.photoOrderService.getPhotoOrders$(_marketingOrderId).pipe(
          map((photoOrders) => photoOrders.find((po) => po.status !== PhotoOrderStatus.CANCELLED)),
          first(),
        ).toPromise();

        const totalPhotos = (photoOrder?.photos?.length ?? 0) + photographerPhotosCount + (files?.length ?? 0);

        if (totalPhotos > this.MAX_PHOTOGRAPHER_PHOTOS_COUNT) {
          this.promptDialog.openPrompt('Error', `You have exceeded the maximum limit of ${this.MAX_PHOTOGRAPHER_PHOTOS_COUNT} photos per listing.`, 'Ok');
          return true;
        }
      }
    } else if (totalOrderPhotos + files.length > this.MAX_ORDER_PHOTOS_COUNT) {
      this.promptDialog.openPrompt('Error', `You have exceeded the maximum limit of ${this.MAX_ORDER_PHOTOS_COUNT} photos per listing`, 'Ok');
      return true;
    }
    return false;
  }

  isPhotoOrderExist(marketingOrder: MarketingOrder): boolean {
    const photographyProduct = marketingOrder.getProduct(ProductCode.PROFESSIONAL_PHOTOGRAPHY);
    return photographyProduct != null && !photographyProduct.optedOut;
  }

  /**
   * Verifies CloudFront URLs are available and emits results for verified URLs.
   * Checks all URLs in parallel with 500ms retries up to 5 seconds max per URL.
   * @param results The upload results from the backend
   */
  private async verifyAndEmitUrls(results: UploadPhoto[]): Promise<void> {
    const MAX_RETRY_TIME_MS = 10000; // 10 seconds max per URL
    const RETRY_INTERVAL_MS = 500; // Retry every 500ms
    const thumbnailSize = 540;
    // Check each URL in parallel with retries
    const urlChecks = results.filter((item) => item.width === thumbnailSize).map(async (item) => {
      const startTime = Date.now();
      let isAvailable = false;

      // eslint-disable-next-line no-await-in-loop
      while (Date.now() - startTime < MAX_RETRY_TIME_MS && !isAvailable) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetch(item.url, { method: 'HEAD' });
          if (response.ok) {
            isAvailable = true;
            return;
          }
        } catch (error) {
          // Continue retrying on error
        }

        // Wait before next retry
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, RETRY_INTERVAL_MS);
        });
      }
    });

    await Promise.all(urlChecks);
    this.uploadResult.emit(results);
  }

  uploadAllWithStatuses(loadArray: any[]) {
    this.statuses = (loadArray || []).map((la) => {
      const uploadStatusObj = new UploadStatus(la.file);
      uploadStatusObj.presignURL = la.url;
      uploadStatusObj.publicURL = la.publicUrl;
      return uploadStatusObj;
    });

    const minFileSizeMB = this.minimumPhotoSizeRequired ? 0.3 : 0;
    const uploads$ = this.statuses.map((status) => this.photoService.preSignUploadWithStatus(status, minFileSizeMB));

    // On the UploadProgress event, update the progress status
    this.loaderService.show();
    forkJoin(uploads$)
      .subscribe(async (statuses) => {
        // Resume Timer
        this.activityService.startWatching();

        this.failedUploads = statuses.filter((status) => status.error).map((status) => `${status.file.name} : ${status.error}`);
        this.successfulUploads = statuses.filter((status) => !status.error).map((status) => status.file.name);

        if (this.failedUploads.length > 0) {
          this.uploadResults.openResults(this.failedUploads, this.successfulUploads, true).then(() => this.clearUploadStatuses());
        } else {
          this.clearUploadStatuses();
        }
        const thumbnailsRequest = {};

        for (const eachLoadArr of loadArray) {
          const failedUploadImageNames = statuses.filter((status) => status.error).map((status) => status.file.name);
          if (!failedUploadImageNames.find((fileName) => fileName === eachLoadArr.name)) {
            thumbnailsRequest[eachLoadArr.name] = eachLoadArr.publicUrl;
          }
        }

        const results = await this.uploadService.postImageUploadAndGetThumbnails(thumbnailsRequest).toPromise()
          .catch((error) => {
            // Resume Timer
            this.activityService.startWatching();
            this.loaderService.hide();
            throw new Error(error);
          });

        // Attach image info and verify URLs
        results.forEach((result) => {
          const fileStatus = statuses.find((s) => s.file.name === result.file);
          result.imageInfo = fileStatus?.imageInfo;
        });
        await this.verifyAndEmitUrls(results);
        this.loaderService.hide();
      }, (error) => {
        // Resume Timer
        this.activityService.startWatching();
        this.loaderService.hide();
        throw new Error(error);
      });
  }

  uploadFiles(files: File[]) {
    // Files is of type FileList, that does not support the simple files.map. Need to use spread operator
    const actualFiles = Array.from(files);
    const filesToPresign = {
      files: actualFiles.map((s) => {
        return {
          name: s.name,
          mimetype: s.type,
          marketingOrderId: this.marketingOrderId,
        };
      }),
    };

    this.activityService.stopWatching();

    this.uploadService.getPresignedUrlForAll(filesToPresign)
      .subscribe(
        (next) => {
          const loadArray = [];
          for (const fileIter in next) {
            if (fileIter) {
              const presignedData = {
                file: Array.from(files).find((f) => f.name === fileIter),
                url: next[fileIter],
                publicUrl: next[`${fileIter}_cloudfront-url`],
                name: fileIter,
              };
              if (presignedData.file) loadArray.push(presignedData);
            }
          }
          this.uploadAllWithStatuses(loadArray);
        },
        (error) => {
          this.activityService.startWatching();
          throw new Error(error);
        },
        () => { this.input.nativeElement.value = ''; },
      );
  }

  private clearUploadStatuses() {
    this.statuses = [];
    this.isProcessing = false;
  }

  public chooseFiles() {
    const element: HTMLElement = document.getElementById('uploadPhotos') as HTMLElement;
    element.click();
  }
}
